import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import RecommendationTip from '../components/RecommendationTip';
import '../styles/EnterpriseDevelopmentScorecard.scss';
import { themeVars } from '../config/theme';

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
const fmtCurrency = (n) => `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const rowClass = (actual, target) => {
  if (actual === null || actual === 0) return 'sc-row--empty';
  return actual >= target ? 'sc-row--achieved' : 'sc-row--partial';
};

const ScorecardSection = ({
  title,
  subtitle,
  indicators,
  results,
  totalPoints,
  bonusSelected,
  spendSummary,
  spendTargetLabel,
  npat,
  maxPoints,
  loading,
  calculating,
  filename,
  noDataSheet,
  noNPAT,
  error,
  onRecalculate,
  onBonusToggle,
  onGoToDoc,
}) => {
  const resultMap = results ? Object.fromEntries(results.map((r) => [r.id, r])) : {};
  const displayTotal = totalPoints + (bonusSelected ? 1 : 0);

  return (
    <div className="sc-section">
      <div className="sc-section-heading">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      <div className="sc-upload-bar">
        <div className="sc-upload-info">
          {loading
            ? <span className="sc-upload-hint">Loading…</span>
            : filename
              ? <span className="sc-upload-name">✓ Calculated from: <strong>{filename}</strong></span>
              : <span className="sc-upload-hint">No data sheet found</span>
          }
        </div>
        <div className="sc-upload-actions">
          <button className="sc-template-btn" onClick={onGoToDoc}>Go to Documentation</button>
          <button className="sc-upload-btn" onClick={onRecalculate} disabled={calculating || loading}>
            {calculating ? 'Calculating…' : 'Recalculate'}
          </button>
        </div>
      </div>

      {noDataSheet && (
        <div className="sc-error">
          No data sheet uploaded yet. Go to{' '}
          <button className="sc-error-link" onClick={onGoToDoc}>Documentation</button>{' '}
          and upload the <strong>Enterprise &amp; Supplier Development Data Sheet</strong>.
        </div>
      )}
      {noNPAT && (
        <div className="sc-error">
          No NPAT found. Please update your NPAT in{' '}
          <button className="sc-error-link" onClick={() => {}}>Company Information</button>.
        </div>
      )}
      {error && <div className="sc-error">{error}</div>}

      {spendSummary && spendSummary.length > 0 && (
        <div className="sc-spend-table-outer">
          <h2 className="sc-spend-title">NPAT &amp; Spend Summary</h2>
          <div className="sc-table-wrapper">
            <table className="sc-table sc-spend-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>NPAT Used</th>
                  <th>Target ({spendTargetLabel})</th>
                  <th>Interest Free Loan Target</th>
                  <th>Actual Spend</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {spendSummary.map((row) => {
                  const diff = row.spend_diff;
                  return (
                    <tr key={row.category} className={diff > 0 ? 'sc-row--achieved' : 'sc-row--partial'}>
                      <td>{row.category}</td>
                      <td>{npat !== null ? fmtCurrency(npat) : '—'}</td>
                      <td>{fmtCurrency(row.target_spend)}</td>
                      <td>{fmtCurrency(row.loan_target)}</td>
                      <td>{fmtCurrency(row.spend)}</td>
                      <td>
                        <span className={diff > 0 ? 'sc-spend-diff sc-spend-diff--pos' : 'sc-spend-diff sc-spend-diff--neg'}>
                          {diff > 0 ? '+' : ''}{fmtCurrency(diff)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sc-table-outer">
        <div className="sc-table-wrapper">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Compliance Target</th>
                <th>Max Points</th>
                <th>Actual (%)</th>
                <th>Points Achieved</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((ind) => {
                if (ind.bonus) {
                  return (
                    <tr key={ind.id} className={bonusSelected ? 'sc-bonus-row sc-bonus-row--claimed' : 'sc-bonus-row'}>
                      <td>{ind.label}</td>
                      <td>{ind.target}</td>
                      <td>{ind.points}</td>
                      <td>—</td>
                      <td>
                        <button
                          className={`sc-bonus-toggle${bonusSelected ? ' sc-bonus-toggle--active' : ''}`}
                          onClick={() => onBonusToggle(!bonusSelected)}
                        >
                          {bonusSelected ? '✓ Claimed — 1.00' : 'Claim Bonus'}
                        </button>
                      </td>
                    </tr>
                  );
                }
                const r = resultMap[ind.id];
                const actual = r?.actual ?? null;
                const pts = r?.points ?? 0;
                const isMax = pts >= ind.points;
                return (
                  <tr key={ind.id} className={rowClass(actual, ind.target)}>
                    <td>{ind.label}</td>
                    <td>{ind.targetLabel ?? `${ind.target}%`} of NPAT</td>
                    <td>{ind.points}</td>
                    <td>{actual !== null ? `${fmt(actual)}%` : '—'}</td>
                    <td>
                      <span className={`sc-points${isMax ? ' sc-points--max' : pts === 0 ? ' sc-points--zero' : ''}`}>
                        {fmt(pts)}
                      </span>
                      <RecommendationTip
                        ind={ind}
                        actual={actual}
                        pts={pts}
                        spendRow={spendSummary?.find(r => r.program === ind.id)}
                      />
                    </td>
                  </tr>
                );
              })}
              <tr className={`sc-total-row${results?.some(r => r.sub_minimum_met === false) ? ' sc-row--submin-fail' : ''}`}>
                <td colSpan="3">Total</td>
                <td />
                <td>{fmt(displayTotal)} / {maxPoints}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {results?.some(r => r.sub_minimum_met === false) && (
          <div className="sc-submin-icon-wrap">
            <span className="sc-submin-icon-btn">⚠</span>
            <div className="sc-submin-popup">
              Sub-minimum not met — overall scorecard level will be discounted by one level.
            </div>
          </div>
        )}
      </div>

      <div className="sc-summary">
        <div className="sc-summary-card sc-summary-card--total">
          <div className="sc-summary-score">{fmt(displayTotal)} <span>/ {maxPoints}</span></div>
          <div className="sc-summary-label">Total {title} Points</div>
        </div>
      </div>
    </div>
  );
};

const SECTIONS_CONFIG = {
  enterprise_supplier_development: ['enterprise_development', 'supplier_development'],
  enterprise_development_transport: ['enterprise_development_transport'],
};

const emptySection = (elementId) => ({
  elementId,
  label: null,
  indicators: [],
  results: null,
  totalPoints: 0,
  bonusSelected: false,
  filename: null,
  spendSummary: null,
  npat: null,
  loading: true,
  calculating: false,
  error: null,
  noDataSheet: false, 
  noNPAT: false,
  negativeNPAT: false,
});

const contributionsMeta = (indicators) => {
  const ind = (indicators || []).find((i) => i.id ==='Contributions');
  if (!ind) return { spendTargetLabel: '', subtitle: '' };
  return {
    spendTargetLabel: `${ind.target}% of NPAT`,
    subtitle: `Contributions target: ${ind.target}% of NPAT - Max ${ind.points} points`,
  };
};

const ESDScorecard = () => {
  const navigate = useNavigate();
    const { elementKey: rawElementKey } = useParams();
    const elementKey = (rawElementKey || 'enterprise_supplier_development').replace(/-/g, '_');
    const elementIds = SECTIONS_CONFIG[elementKey] || SECTIONS_CONFIG.enterprise_supplier_development;

  const [esdState, setEsdState] = useState(() => elementIds.map(emptySection));
  const [combinedPoints, setCombinedPoints] = useState(null);
  const [maxCombinedPoints, setMaxCombinedPoints] = useState(null);
  const [negativeNPATModal, setNegativeNPATModal] = useState(false);

  const updateSection = (elementId, patch) => {
    setEsdState(sections => sections.map(s => (s.elementId === elementId ? { ...s, ...patch } : s)));
  };

  const applyResult = (elementId, data) => {
    updateSection(elementId, {
      label: data.label ?? null,
      indicators: data.indicators || [],
      results: data.results,
      totalPoints: data.total_points,
      bonusSelected: data.bonus_selected ?? false,
      filename: data.filename,
      spendSummary: data.spend_summary ?? null,
      npat: data.npat ?? null,
      loading: false,
      calculating: false,
      error: null,
      noDataSheet: false,
      noNPAT: false,
      negativeNPAT: false,
    });
  };

  const fetchCombined = () => {
    api.get('/core/scorecard/combined-total/')
      .then(res => {
        setCombinedPoints(res.data.combined_points);
        if (res.data.max_points != null) setMaxCombinedPoints(res.data.max_points);
      })
      .catch(() => {});
  };

  const recalculate = async (elementId) => {
    updateSection(elementId, { calculating: true, error: null, noDataSheet: false, noNPAT: false });
    try {
      const res = await api.post('/core/scorecard/calculate_from_data_sheet/', {element: elementId});
      applyResult(elementId, res.data);
      fetchCombined();
    } catch (err) {
      updateSection(elementId, { calculating: false, results: null, totalPoints: 0, spendSummary: null, filename: null, npat: null });
      if (err.response?.status === 404) updateSection(elementId, {noDataSheet: true});
      else if (err.response?.status === 422 && err.response?.data?.error === 'negative_npat') setNegativeNPATModal(true);
      else if (err.response?.status === 422) updateSection(elementId, { noNPAT: true });
      else updateSection(elementId, { error: err.response?.data?.error || 'Failed to recalculate' });
    }
  };

  const handleBonusToggle = async (elementId, checked) => {
    updateSection(elementId, { bonusSelected: checked });
    try {
      await api.patch(`/core/scorecard/update_flag/?element=${elementId}`, { bonus_selected: checked });
    } catch {
      updateSection(elementId, { bonusSelected: !checked });
    }
  };

  useEffect(() => {
    elementIds.forEach(elementId => {
    api.get(`/core/scorecard/get_scorecard/?element=${elementId}`)
      .then(res => applyResult(elementId, res.data))
      .catch(err => {
        updateSection(elementId, {
          loading: false,
          label: err.response?.data?.label ?? null,
          indicators: err.response?.data?.indicators?.length ? err.response.data.indicators : [],
         });
        if (err.response?.status === 422 && err.response?.data?.error === 'negative_npat') setNegativeNPATModal(true);
        else if (err.response?.status === 404) recalculate(elementId);
      });
    });

    fetchCombined();
  }, [elementKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const isTransport = elementKey === 'enterprise_development_transport';
  const submitPath = isTransport
    ? '/esd/enterprise-supplier-development/submit-datasheet/enterprise-development-transport'
    : '/esd/enterprise-supplier-development/submit-datasheet';
  const goToDoc = () => navigate(submitPath);

  const combinedTotal = esdState.reduce((sum, s) => sum + s.totalPoints + (s.bonusSelected ? 1: 0), 0);
  const combinedMax = esdState.reduce(
    (sum, s) => sum + (s.indicators || []).reduce((s2, ind) => s2 + (ind.points || 0), 0),
    0
  );

  return (
    <div className="sc-container themed" style={themeVars(elementKey)}>
      <div className="sc-header">
        <button className="sc-back" onClick={() => navigate(isTransport ? '/esd/enterprise-supplier-development/enterprise-development-transport' : '/esd/enterprise-supplier-development')}>← Back</button>
        <div>
          <h1>{isTransport ? 'Enterprise Development Scorecard' : 'Enterprise &amp; Supplier Development Scorecard'}</h1>
          <p>B-BBEE — {isTransport ? 'ED' : 'ED & SD'} Element Calculation</p>
        </div>
      </div>

      {negativeNPATModal && (
        <div className="sc-modal-overlay">
          <div className="sc-modal">
            <h3 className="sc-modal-title">⚠ Negative NPAT</h3>
            <p className="sc-modal-body">
              Your current NPAT is negative. Targets will be based on the Indicative or Industry Norm.
            </p>
            <div className="sc-modal-actions">
              <button className="sc-modal-btn sc-modal-btn--primary" onClick={() => navigate('/finance-statutory/npat')}>
                Go to NPAT
              </button>
              <button className="sc-modal-btn sc-modal-btn--secondary" onClick={() => setNegativeNPATModal(false)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {esdState.map((section, i) => {
        const maxPoints = (section.indicators || []).reduce((s, ind) => s + (ind.points || 0), 0);
        const { spendTargetLabel, subtitle } = contributionsMeta(section.indicators);
        return (
          <React.Fragment key ={section.elementId}>
            <ScorecardSection
            title={section.label || ''}
            subtitle={subtitle}
            indicators={section.indicators}
            results={section.results}
            totalPoints={section.totalPoints}
            bonusSelected={section.bonusSelected}
            spendSummary={section.spendSummary}
            spendTargetLabel={spendTargetLabel}
            npat={section.npat}
            maxPoints={maxPoints || '-'}
            loading={section.loading}
            calculating={section.calculating}
            filename={section.filename}
            noDataSheet={section.noDataSheet}
            noNPAT={section.noNPAT}
            error={section.error}
            onRecalculate={() =>recalculate(section.elementId)}
            onBonusToggle={(checked) => handleBonusToggle(section.elementId, checked)}
            onGoToDoc={goToDoc}
          />
          {i < esdState.length - 1 && <div className="sc-section-divider" />}
          </React.Fragment>
        );
      })}

      <div className="sc-summary sc-summary--combined">
        <div className="sc-summary-card sc-summary-card--total">
          <div className="sc-summary-score">{fmt(combinedTotal)} <span>/ {(combinedMax) || '—'}</span></div>
          <div className="sc-summary-label">Total {isTransport ? 'ED' : 'ED & SD'} Points</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{combinedPoints !== null ? fmt(combinedPoints) : '—'} <span>/ {maxCombinedPoints ?? '—'}</span></div>
          <div className="sc-summary-label">Combined Scorecard Points</div>
        </div>
      </div>
    </div>
  );
};

export default ESDScorecard;
